// Stylesheets
import './Hero.scss';

// React Core
import React, { useRef, useState, useEffect, useId } from 'react';
import { AnimatePresence, motion } from 'motion/react';

// Routing
import { useNavigate } from 'react-router-dom';

// Components
import { Button } from '../../components/Button/Button';
import { TextAnimate } from 'src/components/Animation/text-animate';
import { Experience } from '../Experience/Experience';
import { ClientType } from '../ClientType/ClientType';
import { Offer } from '../Offer/Offer';
import { Schedule } from '../Schedule/Schedule';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import SEO from 'src/components/SEO';
import { auth } from 'src/config/firebase';

import { useGetOnboardingState } from 'src/api/mutations/useGetOnboardingState';
import { getUserIdFromToken } from 'src/utils/authUtils';

// GSAP
import gsap from 'src/utils/gsapSetup';

// Store
import { sessionStore } from 'src/store/sessionStore';
import { uiStore } from 'src/store/uiStore';

// Assets
import MiniPTGIF from 'url:../../../../assets/videos/mini-pt-care.mp4';
import { ReactComponent as RightArrowIcon } from '../../../../assets/images/onboarding-trainer/right-arrow.svg';
import featureProgramBuilder from 'url:../../../../assets/images/onboarding-trainer/landing/feature-program-builder-transparent.png';
import featureAssignClients from 'url:../../../../assets/images/onboarding-trainer/landing/feature-assign-clients-transparent.png';
import featureAssignDashboard from 'url:../../../../assets/images/onboarding-trainer/landing/feature-assign-dashboard.png';
import featureAiCoach from 'url:../../../../assets/images/onboarding-trainer/landing/feature-ai-coach-transparent.png';
import featureWorkDashboard from 'url:../../../../assets/images/onboarding-trainer/landing/feature-work-dashboard.png';
import featureProgressSync from 'url:../../../../assets/images/onboarding-trainer/landing/feature-progress-sync-transparent.png';
import featureSyncList from 'url:../../../../assets/images/onboarding-trainer/landing/feature-sync-list.png';
import { ReactComponent as SyncPerformanceArt } from '../../../../assets/images/onboarding-trainer/landing/feature-sync-performance.svg';
import featureGrowEarn from 'url:../../../../assets/images/onboarding-trainer/landing/feature-grow-earn.png';
import { ReactComponent as FeatureIconProgramBuilder } from '../../../../assets/images/onboarding-trainer/landing/about-icons/program-builder.svg';
import { ReactComponent as FeatureIconAssignClients } from '../../../../assets/images/onboarding-trainer/landing/about-icons/assign-clients.svg';
import { ReactComponent as FeatureIconClientsWork } from '../../../../assets/images/onboarding-trainer/landing/about-icons/clients-work.svg';
import { ReactComponent as FeatureIconProgressSync } from '../../../../assets/images/onboarding-trainer/landing/about-icons/progress-sync.svg';
import ctaPrograms from 'url:../../../../assets/images/onboarding-trainer/landing/cta-programs.png';
import testimonialEla from 'url:../../../../assets/images/onboarding-trainer/landing/testimonials/testimonial-ela.jpeg';
import testimonialAlex from 'url:../../../../assets/images/onboarding-trainer/landing/testimonials/testimonial-alex.jpeg';
import testimonialSteph from 'url:../../../../assets/images/onboarding-trainer/landing/testimonials/testimonial-steph.jpeg';
import { ReactComponent as QuoteIcon } from '../../../../assets/images/onboarding-trainer/landing/testimonials/quote-icon.svg';
import faqExpandIcon from 'url:../../../../assets/images/onboarding-trainer/landing/faq/faq-expand.png';
import faqCollapseIcon from 'url:../../../../assets/images/onboarding-trainer/landing/faq/faq-collapse.png';
import heroPerson1 from 'url:../../../../assets/images/onboarding-trainer/landing/hero/hero-person-1.jpeg';
import heroPerson2 from 'url:../../../../assets/images/onboarding-trainer/landing/hero/hero-person-2.jpeg';
import heroPerson3 from 'url:../../../../assets/images/onboarding-trainer/landing/hero/hero-person-3.jpeg';
import heroPerson4 from 'url:../../../../assets/images/onboarding-trainer/landing/hero/hero-person-4.jpeg';
import heroPerson5 from 'url:../../../../assets/images/onboarding-trainer/landing/hero/hero-person-5.jpeg';
import heroPerson6 from 'url:../../../../assets/images/onboarding-trainer/landing/hero/hero-person-6.jpeg';
import { ReactComponent as HeroTrendIcon } from '../../../../assets/images/onboarding-trainer/landing/hero/trend-up.svg';
import { ReactComponent as HeroQuoteIcon } from '../../../../assets/images/onboarding-trainer/landing/hero/quote.svg';
import { ReactComponent as HeroStarIcon } from '../../../../assets/images/onboarding-trainer/landing/hero/star.svg';
import { ReactComponent as HeroLaptopIcon } from '../../../../assets/images/onboarding-trainer/landing/hero/laptop.svg';
import { ReactComponent as HeroGroupIcon } from '../../../../assets/images/onboarding-trainer/landing/hero/group.svg';
import { ReactComponent as HeroPoundIcon } from '../../../../assets/images/onboarding-trainer/landing/hero/pound.svg';
import { ReactComponent as HeroImpactArrow } from '../../../../assets/images/onboarding-trainer/landing/hero/impact-arrow.svg';
import platformAvatar from 'url:../../../../assets/images/onboarding-trainer/landing/platform/sidebar-avatar.jpeg';
import platformManBack from 'url:../../../../assets/images/onboarding-trainer/landing/platform/man-grey-back.png';
import platformManFront from 'url:../../../../assets/images/onboarding-trainer/landing/platform/man-grey-front.png';
import { ReactComponent as PlatformUnion } from '../../../../assets/images/onboarding-trainer/landing/platform/platform-union.svg';
import { ReactComponent as PlatformButtonSpark } from '../../../../assets/images/onboarding-trainer/landing/platform/button-spark.svg';
import { ReactComponent as PlatformNavLogo1 } from '../../../../assets/images/onboarding-trainer/landing/platform/nav-logo-1.svg';
import { ReactComponent as PlatformNavLogo2 } from '../../../../assets/images/onboarding-trainer/landing/platform/nav-logo-2.svg';
import { ReactComponent as PlatformNavLogo3 } from '../../../../assets/images/onboarding-trainer/landing/platform/nav-logo-3.svg';
import { ReactComponent as PlatformNavLogo4 } from '../../../../assets/images/onboarding-trainer/landing/platform/nav-logo-4.svg';
import { ReactComponent as PlatformNavPlus } from '../../../../assets/images/onboarding-trainer/landing/platform/nav-icon-plus.svg';
import { ReactComponent as PlatformNavClients } from '../../../../assets/images/onboarding-trainer/landing/platform/nav-icon-clients.svg';
import { ReactComponent as PlatformNavFolder } from '../../../../assets/images/onboarding-trainer/landing/platform/nav-icon-folder.svg';
import { ReactComponent as PlatformNavMessage } from '../../../../assets/images/onboarding-trainer/landing/platform/nav-icon-message.svg';
import { ReactComponent as PlatformMiniRing } from '../../../../assets/images/onboarding-trainer/landing/platform/mini-ring.svg';
import { ReactComponent as PlatformClientProfile } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/client_profile.svg';
import { ReactComponent as PlatformMovementInsights } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/movement_insights.svg';
import { ReactComponent as PlatformProgramGoals } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/program_goals.svg';
import { ReactComponent as PlatformProgramSummary } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/program_summary.svg';
import { ReactComponent as PlatformGoalApproach } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/goal_approach.svg';
import { ReactComponent as PlatformInjuriesNotes } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/injuries_notes.svg';
import { ReactComponent as PlatformPlanStructure } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/plan_structure.svg';
import { ReactComponent as PlatformIntensitySetup } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/intensity_setup.svg';
import { ReactComponent as PlatformEquipmentWeight } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/weight.svg';
import { ReactComponent as PlatformEquipmentBench } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/bar_and_bench.svg';
import { ReactComponent as PlatformEquipmentOther } from '../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/other.svg';
import { ReactComponent as PlatformPainPoint0 } from '../../../../assets/images/onboarding-trainer/landing/platform/pain-point-0.svg';
import { ReactComponent as PlatformPainPoint1 } from '../../../../assets/images/onboarding-trainer/landing/platform/pain-point-1.svg';
import { ReactComponent as PlatformPainPoint2 } from '../../../../assets/images/onboarding-trainer/landing/platform/pain-point-2.svg';
import { ReactComponent as PlatformPainPoint3 } from '../../../../assets/images/onboarding-trainer/landing/platform/pain-point-3.svg';
import { ReactComponent as PlatformPainPoint4 } from '../../../../assets/images/onboarding-trainer/landing/platform/pain-point-4.svg';
import { ReactComponent as PlatformPainPoint5 } from '../../../../assets/images/onboarding-trainer/landing/platform/pain-point-5.svg';
import { ReactComponent as PlatformPainPoint6 } from '../../../../assets/images/onboarding-trainer/landing/platform/pain-point-6.svg';
import { ReactComponent as PlatformDonutArrow } from '../../../../assets/images/onboarding-trainer/landing/platform/donut-arrow.svg';

const features = [
  {
    number: '01',
    title: 'AI Program Builder',
    copy: 'AI creates personalised programs in seconds',
    image: featureProgramBuilder,
    Icon: FeatureIconProgramBuilder,
    animation: 'builder',
    alt: 'miniPT AI program builder laptop mockup',
  },
  {
    number: '02',
    title: 'Assign to Clients',
    copy: 'Assign the program to your entire client base in one click',
    image: featureAssignClients,
    Icon: FeatureIconAssignClients,
    animation: 'assign',
    alt: 'miniPT assign clients laptop mockup',
  },
  {
    number: '03',
    title: 'Clients do the Work',
    copy: 'Clients follow the plan in the app and record their workouts.',
    image: featureAiCoach,
    Icon: FeatureIconClientsWork,
    animation: 'work',
    alt: 'miniPT workout details laptop mockup',
  },
  {
    number: '04',
    title: 'Progress Syncs Back',
    copy: 'Performance data and videos sync back to you automatically.',
    image: featureProgressSync,
    Icon: FeatureIconProgressSync,
    animation: 'sync',
    alt: 'miniPT progress sync laptop mockup',
  },
] as const;

type FeatureAnimationType = (typeof features)[number]['animation'];

// Figma 14686:60542 rows registered to the existing 1072 x 605 laptop export.
const builderFields = { x: 458.136, y: 239.052, width: 188.928, height: 34.256, step: 39.44656 };

// Figma ShareModal 14686:43385 in the existing 1060 x 776 laptop export.
const assignModal = { x: 376.425, y: 167.55, width: 299.15, height: 387.45 };

// Figma ShareModal 14686:70782 in the existing 1072 x 605 laptop export.
const workModal = { x: 382.425, y: 67.425, width: 299.15, height: 408.1503 };

const assignMessage =
  'Hey, Emily! This new program starts today, it is designed based on your feedback last time. Let me know what you think!';

const AssignMessageAnimation = () => {
  const inputRef = useRef<HTMLDivElement>(null);
  const [characterCount, setCharacterCount] = useState(0);

  useEffect(() => {
    const modal = inputRef.current?.closest('.trainer-assign-modal');
    const art = modal?.closest('.trainer-assign-art');
    if (!modal || !art) return undefined;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let timer: ReturnType<typeof setTimeout>;
    let started = false;
    let count = 0;

    const typeNextCharacter = () => {
      count += 1;
      setCharacterCount(count);
      if (count < assignMessage.length) {
        const character = assignMessage[count - 1];
        const delay = /[.!]/.test(character)
          ? 280
          : character === ','
            ? 170
            : 35 + (count % 5) * 12;
        timer = setTimeout(typeNextCharacter, delay);
      } else {
        timer = setTimeout(() => art.classList.add('trainer-assign-resetting'), 2000);
      }
    };

    const startTyping = () => {
      if (started) return;
      started = true;
      timer = setTimeout(typeNextCharacter, 300);
    };

    const finishForReducedMotion = () => {
      if (!reducedMotion.matches) return;
      started = true;
      clearTimeout(timer);
      art.classList.remove('trainer-assign-resetting');
      setCharacterCount(assignMessage.length);
    };

    const onPopupEnd = (event: Event) => {
      if (event.target !== modal || reducedMotion.matches) return;

      const animationName = (event as AnimationEvent).animationName;
      if (animationName === 'trainerAssignModalOut') {
        count = 0;
        started = false;
        setCharacterCount(0);
        art.classList.remove('trainer-assign-resetting');
      } else if (animationName === 'trainerAssignModalIn') {
        startTyping();
      }
    };

    modal.addEventListener('animationend', onPopupEnd);
    reducedMotion.addEventListener('change', finishForReducedMotion);
    finishForReducedMotion();
    // Fast Refresh can mount the text after the pop-up has already completed.
    if (modal.getAnimations().some((animation) => animation.playState === 'finished')) {
      startTyping();
    }

    return () => {
      clearTimeout(timer);
      modal.removeEventListener('animationend', onPopupEnd);
      reducedMotion.removeEventListener('change', finishForReducedMotion);
      art.classList.remove('trainer-assign-resetting');
    };
  }, []);

  return (
    <div className="trainer-assign-message" ref={inputRef}>
      <span className="trainer-assign-message-text">
        {characterCount === 0 ? 'Type here...' : assignMessage.slice(0, characterCount)}
        {characterCount > 0 && characterCount < assignMessage.length && (
          <span className="trainer-assign-typing-caret" />
        )}
      </span>
      <span className="trainer-assign-message-count">{characterCount}/200</span>
    </div>
  );
};

// Figma 14686:61620; the PNG export includes all eight rows, including clipped rows.
const syncList = {
  x: 301.25366,
  y: 445.45443,
  width: 284.21147,
  rowHeight: 34.26796,
  step: 40.46895,
};
const syncExercises = [
  'Dynamic Stretching',
  'Jumping Jack',
  'High knees',
  'Flutter kicks',
  'Leg raises',
  'Plank',
  'Abs crunches',
  'Ab crunches',
];

const SyncListAnimation = () => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const clipId = `sync-progress-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const viewport = viewportRef.current;
    const row = viewport?.closest('.trainer-feature-row');
    if (!viewport || !row) return undefined;

    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const cards = Array.from(viewport.querySelectorAll('.trainer-sync-exercise'));
      const stack = viewport.querySelector('.trainer-sync-stack');
      const svg = viewport.querySelector('.trainer-sync-progress svg');
      const score = svg?.querySelector('[data-progress-label] tspan');
      const arcs = Array.from(svg?.querySelectorAll('[data-progress-art] path') ?? []).filter(
        (path) => path.getAttribute('fill')?.toLowerCase() === '#71be41',
      );
      const clip = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
      const sector = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      clip.id = clipId;
      clip.appendChild(sector);
      svg?.appendChild(clip);
      const progress = { value: 0 };
      const updateProgress = () => {
        if (score) score.textContent = `${Math.round(progress.value)}%`;
        // Reveal the original Figma arc from its lower endpoint; never redraw it.
        const sweep = (progress.value / 86) * 290;
        const points = ['63.7564,70.5435'];
        for (let step = 0; step <= 64; step += 1) {
          const angle = ((75 + (sweep * step) / 64) * Math.PI) / 180;
          points.push(`${63.7564 + 150 * Math.cos(angle)},${70.5435 + 150 * Math.sin(angle)}`);
        }
        sector.setAttribute('points', points.join(' '));
        arcs.forEach((arc) => {
          if (progress.value >= 86) arc.removeAttribute('clip-path');
          else arc.setAttribute('clip-path', `url(#${clipId})`);
        });
      };
      score?.setAttribute('x', '63.7694');
      score?.setAttribute('text-anchor', 'middle');
      updateProgress();
      const stepPercent = (syncList.step / syncList.rowHeight) * 100;
      gsap.set(cards, { opacity: 0, yPercent: -stepPercent, y: 0 });
      const timeline = gsap.timeline({ paused: true, repeat: -1, repeatDelay: 0.65 });
      timeline.set(cards, { opacity: 0, yPercent: -stepPercent });
      timeline.set(stack, { opacity: 1 });
      timeline.set(progress, { value: 0, onUpdate: updateProgress }, 0);
      timeline.to(
        progress,
        { value: 86, duration: 3.8, ease: 'power1.inOut', onUpdate: updateProgress },
        0.45,
      );

      for (let index = cards.length - 1; index >= 0; index -= 1) {
        const arrival = 0.45 + (cards.length - 1 - index) * 0.65;
        timeline.to(
          cards[index],
          { opacity: 1, yPercent: 0, duration: 0.4, ease: 'power3.out' },
          arrival,
        );
        for (let older = index + 1; older < cards.length; older += 1) {
          timeline.to(
            cards[older],
            {
              yPercent: (older - index) * stepPercent,
              duration: 0.4,
              ease: 'power3.out',
            },
            arrival,
          );
        }
      }

      timeline.to(stack, { opacity: 0, duration: 0.25 }, '+=2');
      const resetProgress = () => {
        progress.value = 86;
        updateProgress();
        clip.remove();
      };
      if (!('IntersectionObserver' in window)) {
        timeline.play();
        return resetProgress;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            timeline.play();
            observer.disconnect();
          }
        },
        { rootMargin: '0px 0px -18% 0px', threshold: 0.35 },
      );
      observer.observe(row);
      return () => {
        observer.disconnect();
        resetProgress();
      };
    });

    return () => media.revert();
  }, [clipId]);

  return (
    <div className="trainer-sync-motion" ref={viewportRef} aria-hidden="true">
      <div className="trainer-sync-progress">
        <SyncPerformanceArt viewBox="21.7344 28.5215 85 85" />
      </div>
      <div className="trainer-sync-viewport">
        <div className="trainer-sync-stack">
          {syncExercises.map((exercise, index) => (
            <span
              className="trainer-sync-exercise"
              data-exercise={exercise}
              key={exercise}
              style={{
                transform: `translateY(${((index * syncList.step) / syncList.rowHeight) * 100}%)`,
                backgroundImage: `url(${featureSyncList})`,
                backgroundSize: `100% ${(318 / syncList.rowHeight) * 100}%`,
                backgroundPosition: `0 ${((index * syncList.step) / (318 - syncList.rowHeight)) * 100}%`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const FeatureScreenAnimation = ({ type }: { type: FeatureAnimationType }) => {
  if (type === 'builder') {
    return (
      <>
        <span className="trainer-builder-glow" aria-hidden="true" />
        <div className="trainer-builder-fields" aria-hidden="true">
          <span className="trainer-builder-field-glow" />
          {[0, 1, 2, 3, 4].map((index) => {
            const y = builderFields.y + index * builderFields.step;

            return (
              <span
                className="trainer-builder-field-face"
                key={index}
                style={{
                  left: `${(builderFields.x / 1072) * 100}%`,
                  top: `${(y / 605) * 100}%`,
                  width: `${(builderFields.width / 1072) * 100}%`,
                  height: `${(builderFields.height / 605) * 100}%`,
                  backgroundImage: `url(${featureProgramBuilder})`,
                  backgroundSize: `${(1072 / builderFields.width) * 100}% ${(605 / builderFields.height) * 100}%`,
                  backgroundPosition: `${(builderFields.x / (1072 - builderFields.width)) * 100}% ${(y / (605 - builderFields.height)) * 100}%`,
                }}
              />
            );
          })}
        </div>
      </>
    );
  }

  if (type === 'assign') {
    return (
      <>
        <div className="trainer-assign-display" aria-hidden="true">
          <img className="trainer-assign-dashboard" src={featureAssignDashboard} alt="" />
        </div>
        <div
          className="trainer-assign-modal"
          aria-hidden="true"
          style={{
            left: `${(assignModal.x / 1060) * 100}%`,
            top: `${(assignModal.y / 776) * 100}%`,
            width: `${(assignModal.width / 1060) * 100}%`,
            height: `${(assignModal.height / 776) * 100}%`,
            backgroundImage: `url(${featureAssignClients})`,
            backgroundSize: `${(1060 / assignModal.width) * 100}% ${(776 / assignModal.height) * 100}%`,
            backgroundPosition: `${(assignModal.x / (1060 - assignModal.width)) * 100}% ${(assignModal.y / (776 - assignModal.height)) * 100}%`,
          }}
        >
          <AssignMessageAnimation />
        </div>
      </>
    );
  }

  if (type === 'work') {
    return (
      <>
        <div className="trainer-work-display" aria-hidden="true">
          <img className="trainer-work-dashboard" src={featureWorkDashboard} alt="" />
        </div>
        <div
          className="trainer-work-modal"
          aria-hidden="true"
          style={{
            left: `${(workModal.x / 1072) * 100}%`,
            top: `${(workModal.y / 605) * 100}%`,
            width: `${(workModal.width / 1072) * 100}%`,
            height: `${(workModal.height / 605) * 100}%`,
            backgroundImage: `url(${featureAiCoach})`,
            backgroundSize: `${(1072 / workModal.width) * 100}% ${(605 / workModal.height) * 100}%`,
            backgroundPosition: `${(workModal.x / (1072 - workModal.width)) * 100}% ${(workModal.y / (605 - workModal.height)) * 100}%`,
          }}
        />
      </>
    );
  }

  return <SyncListAnimation />;
};

const testimonials = [
  {
    quote:
      'I used to spend hours building programs and chasing client updates. Now I can get a solid starting point in minutes and spend more time actually coaching.',
    name: 'Ela Morrigan',
    role: 'Online Personal Trainer',
    avatar: testimonialEla,
  },
  {
    quote:
      'The biggest difference is how prepared I feel for every client. I can see their progress, what they have missed and what needs attention before we even start the check-in.',
    name: 'Alex Johnson',
    role: 'Strength & Conditioning Coach',
    avatar: testimonialAlex,
  },
  {
    quote:
      'MiniPT helped me turn my in-person clients into an ongoing online service. I can support more people between sessions without adding more admin to my week.',
    name: 'Steph Savyn',
    role: 'Hybrid Fitness Coach',
    avatar: testimonialSteph,
  },
];

const pricingPlans = [
  {
    title: 'Basic Plan',
    price: '\u00a30',
    suffix: '/mo',
    copy: 'Best for trainers who want a simple, organised way to manage clients and build programs manually.',
    features: [
      'Manual program creation',
      'Add and manage clients',
      'Client onboarding',
      'Trainer notes',
      'Convert sketches them into structured programs',
      'Edit workouts manually',
      'Save and manage programs',
    ],
  },
  {
    title: 'Pro Plan',
    price: '\u00a335',
    suffix: '/mo',
    copy: 'Best for trainers who want to save time with AI and track client performance more closely.',
    featured: true,
    features: [
      'All Basic Plan features',
      'AI program generation',
      'AI exercise replacement',
      'Shuffle or regenerate workouts',
      'AI-assisted program changes',
      'Client movement and exercise tracking',
      'Progress insights',
      'Advanced analytics',
    ],
  },
  {
    title: 'Gyms Plan',
    price: 'Talk to Our Team',
    copy: 'For gyms that want to give their personal trainers full access to miniPT Pro at a lower team rate.',
    features: [
      'Everything in Pro',
      'From 6 trainer accounts',
      'Reduced price per trainer',
      'Centralised subscription management',
      'Add or remove trainer seats as your team changes',
      'One monthly invoice for the gym',
      'Priority onboarding and support',
    ],
  },
];

const faqs = [
  {
    question: 'What is miniPT for trainers?',
    answer:
      'miniPT is an online coaching platform that helps trainers create programs, assign workouts, manage clients, and track progress in one place. Its AI tools reduce repetitive planning work while keeping the trainer in control of every program.',
  },
  {
    question: 'Does AI replace the trainer?',
    answer:
      'No. AI creates a strong starting point and handles repetitive admin, while you review, adjust and coach each client.',
  },
  {
    question: 'Can I create workouts manually?',
    answer:
      'Yes. You can build programs manually, edit AI-generated workouts, and save templates for later.',
  },
  {
    question: 'What information does the AI use to create a program?',
    answer:
      'It uses the client profile, goals, experience level, schedule, equipment, injuries and notes you provide.',
  },
  {
    question: 'Can clients complete workouts without me being there?',
    answer:
      'Yes. Clients can follow assigned workouts remotely and keep their progress synced back to you.',
  },
  {
    question: 'What client progress can I track?',
    answer:
      'You can track completed sessions, missed workouts, movement notes, progress trends and program adherence.',
  },
  {
    question: 'Can I make changes after assigning a workout?',
    answer: "Yes. Adjust workouts whenever a client's needs change.",
  },
  {
    question: 'Is there a free plan?',
    answer:
      'Yes. The Basic Plan is free and supports manual program creation and client management.',
  },
  {
    question: 'What is included in the Pro trial?',
    answer:
      'The Pro trial includes AI program generation, AI-assisted changes, progress insights and advanced tracking features.',
  },
];

const AnimatedNumber = ({
  target,
  prefix = '',
  suffix = '',
  delay = 0,
  duration = 1400,
}: {
  target: number;
  prefix?: string;
  suffix?: string;
  delay?: number;
  duration?: number;
}) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setValue(target);
      return undefined;
    }

    let frameId = 0;
    const timeoutId = window.setTimeout(() => {
      const start = performance.now();

      const tick = (now: number) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(target * eased));

        if (progress < 1) {
          frameId = window.requestAnimationFrame(tick);
        }
      };

      frameId = window.requestAnimationFrame(tick);
    }, delay);

    return () => {
      window.clearTimeout(timeoutId);
      window.cancelAnimationFrame(frameId);
    };
  }, [delay, duration, target]);

  return (
    <>
      {prefix}
      {value.toLocaleString('en-GB')}
      {suffix}
    </>
  );
};

const platformTourCards = [
  'client',
  'movement',
  'goals',
  'summary',
  'approach',
  'injuries',
  'plan',
  'intensity',
] as const;

const PlatformIconShell = ({ children }: { children: React.ReactNode }) => (
  <span className="trainer-platform-icon">{children}</span>
);

const PlatformDots = ({ active = 3, total = 5 }: { active?: number; total?: number }) => (
  <span className="trainer-platform-dots">
    {Array.from({ length: total }).map((_, index) => (
      <i className={index < active ? 'is-on' : ''} key={index} />
    ))}
  </span>
);

const PlatformBars = ({ heights }: { heights: number[] }) => (
  <span className="trainer-platform-bars">
    {heights.map((height, index) => (
      <i key={`${height}-${index}`} style={{ height: `${height}%` }} />
    ))}
  </span>
);

const PlatformCard = ({
  id,
  activeId,
  className = '',
  children,
}: {
  id: (typeof platformTourCards)[number];
  activeId: (typeof platformTourCards)[number] | null;
  className?: string;
  children: React.ReactNode;
}) => (
  <article
    className={`trainer-platform-card trainer-platform-card-${id} ${
      activeId === id ? 'is-spotlight' : activeId ? 'is-dimmed' : ''
    } ${className}`}
  >
    <div className="trainer-platform-card-float">{children}</div>
  </article>
);

const TrainerPlatformDemo = ({ isActive }: { isActive: boolean }) => {
  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isActive) {
      setActiveCardIndex(null);
      return undefined;
    }

    const startTimer = window.setTimeout(() => {
      setActiveCardIndex(0);
    }, 3300);

    const interval = window.setInterval(() => {
      setActiveCardIndex((current) => {
        if (current === null) return 0;
        return (current + 1) % platformTourCards.length;
      });
    }, 2900);

    return () => {
      window.clearTimeout(startTimer);
      window.clearInterval(interval);
    };
  }, [isActive]);

  const activeId =
    activeCardIndex === null ? null : platformTourCards[activeCardIndex % platformTourCards.length];

  return (
    <div className="trainer-platform-stage" aria-label="miniPT trainer program review platform">
      <PlatformUnion className="trainer-platform-union" aria-hidden="true" />
      <div className="trainer-platform-shell">
        <aside className="trainer-platform-sidebar" aria-hidden="true">
          <div className="trainer-platform-logo">
            <PlatformNavLogo1 />
            <PlatformNavLogo2 />
            <PlatformNavLogo3 />
            <PlatformNavLogo4 />
          </div>
          <div className="trainer-platform-nav-main">
            <span>
              <PlatformNavPlus />
            </span>
          </div>
          <div className="trainer-platform-nav-list">
            <span>
              <PlatformNavClients />
            </span>
            <span>
              <PlatformNavFolder />
            </span>
            <span>
              <PlatformNavMessage />
            </span>
          </div>
          <span className="trainer-platform-avatar">
            <img src={platformAvatar} alt="" />
          </span>
        </aside>

        <div className="trainer-platform-main">
          <div className="trainer-platform-top">
            <div className="trainer-platform-ring">
              <PlatformMiniRing />
              <span>miniPT</span>
            </div>
            <div className="trainer-platform-title">
              <strong>Review the program before we create it</strong>
              <span>Tap any card to adjust details before generating the program</span>
            </div>
          </div>

          <div className="trainer-platform-cards">
            <PlatformCard id="client" activeId={activeId}>
              <div className="trainer-platform-card-head">
                <PlatformIconShell>
                  <PlatformClientProfile />
                </PlatformIconShell>
                <strong>Client Profile</strong>
              </div>
              <div className="trainer-platform-row">Advanced</div>
              <div className="trainer-platform-row">
                <span>
                  Energy: <strong>Fluctuates</strong>
                </span>
                <PlatformBars heights={[42, 58, 70, 92, 76, 88, 82, 86]} />
              </div>
              <div className="trainer-platform-row">
                <span>
                  Activity: <strong>Lightly Active</strong>
                </span>
                <PlatformDots active={3} total={6} />
              </div>
            </PlatformCard>

            <PlatformCard id="movement" activeId={activeId}>
              <div className="trainer-platform-card-head">
                <PlatformIconShell>
                  <PlatformMovementInsights />
                </PlatformIconShell>
                <strong>Movement Insights</strong>
              </div>
              {[
                ['Mobility', 'Good', 3],
                ['Stability', 'Limited', 2],
                ['Balance', 'Good', 3],
                ['Movement Symmetry', 'Good', 3],
                ['Movement Quality', 'Good', 3],
              ].map(([label, value, dots]) => (
                <div className="trainer-platform-row" key={label}>
                  <span>
                    {label}: <strong>{value}</strong>
                  </span>
                  <PlatformDots active={Number(dots)} total={3} />
                </div>
              ))}
            </PlatformCard>

            <PlatformCard id="goals" activeId={activeId}>
              <div className="trainer-platform-card-head">
                <PlatformIconShell>
                  <PlatformProgramGoals />
                </PlatformIconShell>
                <strong>Program Goals</strong>
              </div>
              <div className="trainer-platform-goals-body">
                <div>
                  <p className="trainer-platform-orange">• Build Muscle</p>
                  <p>
                    Priority: <strong>Core, Back, Glutes</strong>
                  </p>
                  <p>
                    <strong>• Secondary Support:</strong>
                  </p>
                  <p>Mobility, Injury Prevention</p>
                </div>
                <div className="trainer-platform-donut">
                  <svg
                    className="trainer-platform-donut-ring"
                    viewBox="0 0 72 72"
                    aria-hidden="true"
                  >
                    <circle className="trainer-platform-donut-track" cx="36" cy="36" r="29" />
                    <circle className="trainer-platform-donut-progress" cx="36" cy="36" r="29" />
                  </svg>
                  <PlatformDonutArrow className="trainer-platform-donut-arrow" />
                  <span>85%</span>
                  <small>15%</small>
                </div>
              </div>
            </PlatformCard>

            <PlatformCard id="summary" activeId={activeId} className="is-default-focus">
              <div className="trainer-platform-card-head">
                <PlatformIconShell>
                  <PlatformProgramSummary />
                </PlatformIconShell>
                <strong>Alvin Bennett&nbsp;&nbsp;•&nbsp;&nbsp;Program Summary</strong>
              </div>
              <p className="trainer-platform-summary-copy">
                5-week muscle-building program for an advanced client, focused on core, back and
                glutes, adapted for back and shoulder limitations.
              </p>
              <div className="trainer-platform-tags">
                <span>5 Weeks</span>
                <span>3 Days / Week</span>
                <span>45 min</span>
              </div>
              <button className="trainer-platform-ai-button" type="button">
                <PlatformButtonSpark />
                Generate with miniPT AI
              </button>
            </PlatformCard>

            <PlatformCard id="approach" activeId={activeId}>
              <div className="trainer-platform-card-head">
                <PlatformIconShell>
                  <PlatformGoalApproach />
                </PlatformIconShell>
                <strong>Goal Approach</strong>
              </div>
              <div className="trainer-platform-two-col">
                <div>
                  <p>Full-Body</p>
                  <p>Hypertrophy</p>
                </div>
                <PlatformBars heights={[36, 48, 70, 58, 76, 44, 34, 62, 78, 56]} />
              </div>
            </PlatformCard>

            <PlatformCard id="injuries" activeId={activeId}>
              <div className="trainer-platform-card-head">
                <PlatformIconShell>
                  <PlatformInjuriesNotes />
                </PlatformIconShell>
                <strong>Injuries &amp; Notes</strong>
              </div>
              <div className="trainer-platform-injuries-body">
                <div>
                  <p className="trainer-platform-red">Back, Shoulders</p>
                  <p>Avoid overhead press</p>
                  <p>No jumping</p>
                </div>
                <div className="trainer-platform-body-stack" aria-hidden="true">
                  <span className="trainer-platform-body-card trainer-platform-body-card-back">
                    <img src={platformManBack} alt="" />
                    <PlatformPainPoint0 className="pain pain-0" />
                    <PlatformPainPoint1 className="pain pain-1" />
                    <PlatformPainPoint2 className="pain pain-2" />
                  </span>
                  <span className="trainer-platform-body-card trainer-platform-body-card-front">
                    <img src={platformManFront} alt="" />
                    <PlatformPainPoint3 className="pain pain-3" />
                    <PlatformPainPoint4 className="pain pain-4" />
                    <PlatformPainPoint5 className="pain pain-5" />
                    <PlatformPainPoint6 className="pain pain-6" />
                  </span>
                </div>
              </div>
            </PlatformCard>

            <PlatformCard id="plan" activeId={activeId}>
              <div className="trainer-platform-card-head">
                <PlatformIconShell>
                  <PlatformPlanStructure />
                </PlatformIconShell>
                <strong>Plan Structure</strong>
              </div>
              <div className="trainer-platform-plan-body">
                <div>
                  <p>3 Days / Week</p>
                  <p>Evening reminder</p>
                </div>
                <div className="trainer-platform-calendar" aria-hidden="true">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                    <span key={`${day}-${index}`}>{day}</span>
                  ))}
                  {Array.from({ length: 14 }).map((_, index) => (
                    <i className={[0, 2, 4].includes(index) ? 'is-on' : ''} key={index} />
                  ))}
                </div>
              </div>
            </PlatformCard>

            <PlatformCard id="intensity" activeId={activeId}>
              <div className="trainer-platform-card-head">
                <PlatformIconShell>
                  <PlatformIntensitySetup />
                </PlatformIconShell>
                <strong>Intensity &amp; Setup</strong>
              </div>
              <div className="trainer-platform-row">
                <span>
                  Intensity: <strong>Balanced</strong>
                </span>
                <PlatformDots active={3} total={6} />
              </div>
              <div className="trainer-platform-row">
                <span>Dumbbells</span>
                <span className="trainer-platform-equipment-icon" aria-hidden="true">
                  <PlatformEquipmentWeight />
                </span>
              </div>
              <div className="trainer-platform-row">
                <span>Machines &amp; Cables</span>
                <span className="trainer-platform-equipment-icon" aria-hidden="true">
                  <PlatformEquipmentBench />
                </span>
              </div>
              <div className="trainer-platform-row">
                <span>Other +2</span>
                <span className="trainer-platform-equipment-icon" aria-hidden="true">
                  <PlatformEquipmentOther />
                </span>
              </div>
            </PlatformCard>
          </div>
        </div>
      </div>
    </div>
  );
};

const Step6AIHelp = ({ onComplete }: { onComplete: () => void }) => {
  const [index, setIndex] = useState(0);

  const AI_HELP_TEXTS = [
    'Automates check-ins, personalises\nsubstitutions, accelerates prog refresh',
    'Streamlines program creation,\nsuggests progression paths & client\nmessaging',
    'Accelerates programme creation,\nauto-adjustments & client follow-ups',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => {
        if (prev >= AI_HELP_TEXTS.length - 1) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="animation-content max-width-768 step6-ai-help-container">
      <p
        style={{
          fontSize: '18px',
          color: 'var(--auth-text-muted)',
          marginBottom: '20px',
          fontWeight: 400,
          padding: '0 20px',
        }}
      >
        Based on your profile, miniPT AI will help with
      </p>
      <div
        style={{
          minHeight: '160px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          width: '100%',
          padding: '0 20px',
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            style={{ textAlign: 'center' }}
          >
            <h2
              className="page-intro-title mt-0"
              style={{
                fontSize: '32px',
                lineHeight: '1.4',
                whiteSpace: 'pre-wrap',
                fontWeight: 600,
              }}
            >
              {AI_HELP_TEXTS[index]}
            </h2>
          </motion.div>
        </AnimatePresence>
      </div>
      <Button variant="primary" className="btn-primary-white" onClick={onComplete}>
        <span style={{ color: '#000', fontWeight: 'bold' }}>Great, let&apos;s go!</span>
      </Button>
    </div>
  );
};

export const Hero = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [activePricingIndex, setActivePricingIndex] = useState<number | null>(null);
  const [isDemoInView, setIsDemoInView] = useState(false);
  const landingPageRef = useRef<HTMLDivElement>(null);
  const demoSectionRef = useRef<HTMLElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoTextRef = useRef<HTMLSpanElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    }
  }, [step]);

  const isLoggedIn = sessionStore((s) => s.isLoggedIn);
  const token = sessionStore((s) => s.token);
  const currentUser = sessionStore((s) => s.currentUser);
  const hasSignedUp = uiStore((s) => s.hasSignedUp);

  const userId = getUserIdFromToken(token) || auth.currentUser?.uid || currentUser?.uid;

  const { data, isLoading } = useGetOnboardingState(userId || undefined);
  const onboardingState = data?.onboarding;
  const profile = data?.profile;

  const firstName = profile?.name ? profile.name.split(' ')[0] : 'Trainer';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  useEffect(() => {
    const headerEl = document.getElementById('floating-header');
    if (headerEl) {
      if (step >= 2) {
        headerEl.style.display = 'none';
      } else {
        headerEl.style.display = '';
      }
    }
    return () => {
      const el = document.getElementById('floating-header');
      if (el) el.style.display = '';
    };
  }, [step]);

  useEffect(() => {
    const demoSection = demoSectionRef.current;

    if (!demoSection || !('IntersectionObserver' in window)) {
      setIsDemoInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsDemoInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(demoSection);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const featureRows = landingPageRef.current?.querySelectorAll<HTMLElement>(
      '.trainer-feature-row[data-feature-animation]',
    );

    if (!featureRows?.length) return undefined;

    if (!('IntersectionObserver' in window)) {
      featureRows.forEach((row) => row.classList.add('trainer-feature-row-active'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('trainer-feature-row-active');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '0px 0px -18% 0px',
        threshold: 0.35,
      },
    );

    featureRows.forEach((row) => observer.observe(row));

    return () => observer.disconnect();
  }, []);

  const handleGetStarted = () => {
    if (!isLoggedIn) {
      if (hasSignedUp) {
        navigate('/onboarding-trainer/login');
      } else {
        navigate('/onboarding-trainer/signup');
      }
      return;
    }

    if (step === 2 || step > 2) return;

    const tl = gsap.timeline({
      onComplete: () => {
        setStep(2);
      },
    });

    tl.to(
      landingPageRef.current,
      {
        y: -30,
        opacity: 0,
        duration: 0.5,
        ease: 'power2.inOut',
        onComplete: () => {
          if (landingPageRef.current) gsap.set(landingPageRef.current, { display: 'none' });
        },
      },
      0,
    );

    const isMobile = window.innerWidth < 768;
    tl.to(
      videoContainerRef.current,
      {
        yPercent: -50,
        xPercent: -50,
        y: isMobile ? -80 : -90,
        scale: 1,
        width: isMobile ? 160 : 240,
        height: isMobile ? 160 : 240,
        opacity: 1,
        duration: 1.5,
        ease: 'power3.inOut',
      },
      0.2,
    );

    tl.to(
      videoTextRef.current,
      {
        opacity: 1,
        duration: 0.5,
        ease: 'power2.out',
      },
      1.2,
    );
  };

  const scrollToDemo = () => {
    document.getElementById('trainer-demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (videoContainerRef.current && step === 1) {
      gsap.set(videoContainerRef.current, {
        xPercent: -50,
        yPercent: -50,
        y: '70vh',
        scale: 4,
        opacity: 0,
      });
      if (window.innerWidth < 576) {
        gsap.set(videoContainerRef.current, {
          width: 150,
          height: 150,
          top: '40%',
        });
      }
    }
    if (videoTextRef.current && step === 1) {
      gsap.set(videoTextRef.current, { opacity: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isLoggedIn &&
      !isLoading &&
      step === 1 &&
      videoContainerRef.current &&
      landingPageRef.current
    ) {
      const savedStep =
        onboardingState?.lastCompletedStep ?? profile?.onboarding?.lastCompletedStep ?? 0;

      if (savedStep >= 4) {
        navigate('/onboarding-trainer/dashboard', { replace: true });
        return;
      }

      if (savedStep >= 1) {
        gsap.set(landingPageRef.current, { display: 'none' });
        gsap.set(videoContainerRef.current, {
          top: '94px',
          y: 0,
          yPercent: 0,
          xPercent: -50,
          scale: 1,
          width: 240,
          height: 240,
          opacity: 1,
        });
        if (videoTextRef.current) gsap.set(videoTextRef.current, { opacity: 1 });

        if (savedStep === 1) setStep(4);
        if (savedStep === 2) setStep(5);
        if (savedStep === 3) setStep(6);
      } else {
        // Brand new logged-in trainer (savedStep === 0):
        // Automatically start the onboarding flow without showing landing page
        gsap.set(landingPageRef.current, { display: 'none' });
        setStep(2);
      }
    }
  }, [isLoggedIn, isLoading, navigate, onboardingState, profile, step, currentUser]);

  useEffect(() => {
    if (step === 2) {
      if (videoContainerRef.current) {
        const isMobile = window.innerWidth < 768;
        gsap.set(videoContainerRef.current, {
          top: '50%',
          left: '50%',
          xPercent: -50,
          yPercent: -50,
          y: isMobile ? -80 : -90,
          scale: 1,
          width: isMobile ? 160 : 240,
          height: isMobile ? 160 : 240,
          opacity: 1,
        });
      }
      if (videoTextRef.current) {
        gsap.set(videoTextRef.current, { opacity: 1 });
      }
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      }
    } else if (step >= 3 && videoContainerRef.current) {
      const isMobile = window.innerWidth < 768;
      gsap.set(videoContainerRef.current, {
        top: isMobile ? '16px' : '40px',
        left: '50%',
        y: 0,
        yPercent: 0,
        xPercent: -50,
        scale: 1,
        width: isMobile ? 130 : 200,
        height: isMobile ? 130 : 200,
        opacity: 1,
      });
      if (videoTextRef.current) {
        gsap.set(videoTextRef.current, { opacity: 1 });
      }
    }
  }, [step]);

  useEffect(() => {
    const handleResize = () => {
      if (step >= 3 && videoContainerRef.current) {
        const isMobile = window.innerWidth < 768;
        gsap.set(videoContainerRef.current, {
          top: isMobile ? '16px' : '40px',
          left: '50%',
          y: 0,
          yPercent: 0,
          xPercent: -50,
          scale: 1,
          width: isMobile ? 130 : 200,
          height: isMobile ? 130 : 200,
          opacity: 1,
        });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [step]);

  // While checking session state for a logged-in user on initial mount, prevent flashing Step 1
  if (
    isLoggedIn &&
    step === 1 &&
    (isLoading ||
      (currentUser &&
        !onboardingState &&
        (currentUser.experience !== undefined || currentUser.expertise)))
  ) {
    return (
      <div
        className="onboarding-hero"
        style={{
          height: '100vh',
          backgroundColor: '#060723',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#F59E0B' }} spin />} />
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Become a miniPT Trainer"
        description="Join the miniPT platform and take your personal training business to the next level."
        canonicalUrl="/onboarding-trainer"
      />
      <div
        className={`onboarding-hero ${step === 2 ? 'step-2-active' : ''} ${step >= 3 ? 'step-3-active' : ''}`}
      >
        {step === 1 && (
          <div className="trainer-landing-page" ref={landingPageRef}>
            <section className="trainer-hero" id="trainers">
              <div className="trainer-landing-container trainer-hero-grid">
                <div className="trainer-hero-copy">
                  <p className="trainer-eyebrow">
                    Build for trainer. <strong>Powered by AI</strong>
                  </p>
                  <h1>Turn online coaching into recurring revenue</h1>
                  <p className="trainer-lead">
                    Create programs with AI, assign them remotely and track automatically, so you
                    can help more clients and earn more, every month.
                  </p>
                  <div className="trainer-actions">
                    <Button
                      variant="primary"
                      onClick={handleGetStarted}
                      svg={<RightArrowIcon />}
                      className="trainer-button trainer-button-primary right-arrow"
                    >
                      Get Started
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={scrollToDemo}
                      className="trainer-button trainer-button-dark"
                    >
                      Try Demo
                    </Button>
                  </div>
                </div>

                <div
                  className="trainer-hero-visual"
                  aria-label="miniPT trainer business dashboard preview"
                >
                  <div className="trainer-hero-card-cluster">
                    <article className="trainer-hero-animated-card trainer-revenue-card">
                      <div className="trainer-hero-card-float">
                        <div className="trainer-card-label">
                          <span className="trainer-card-icon">
                            <HeroTrendIcon />
                          </span>
                          Monthly online revenue
                        </div>
                        <div className="trainer-card-value">
                          <AnimatedNumber target={4860} prefix="£" delay={1500} duration={1600} />
                        </div>
                        <div className="trainer-card-delta">
                          ↑ <AnimatedNumber target={24} delay={1500} duration={1600} />% vs last
                          month
                        </div>
                        <svg className="trainer-spark" viewBox="0 0 220 46" aria-hidden="true">
                          <defs>
                            <linearGradient id="trainerSparkFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#ffa100" stopOpacity="0.45" />
                              <stop offset="100%" stopColor="#ffa100" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <path
                            className="trainer-spark-fill"
                            d="M0,34 L20,30 L45,33 L70,20 L95,24 L120,14 L145,18 L170,8 L195,12 L220,4 L220,46 L0,46 Z"
                          />
                          <path
                            className="trainer-spark-line"
                            d="M0,34 L20,30 L45,33 L70,20 L95,24 L120,14 L145,18 L170,8 L195,12 L220,4"
                          />
                        </svg>
                      </div>
                    </article>

                    <article className="trainer-hero-animated-card trainer-review-card">
                      <div className="trainer-hero-card-float">
                        <span className="trainer-quote-mark">
                          <HeroQuoteIcon />
                        </span>
                        <p>I added online clients without adding more gym hours.</p>
                        <span className="trainer-hero-divider" />
                        <div className="trainer-hero-person">
                          <span className="trainer-hero-avatar">
                            <img src={heroPerson1} alt="" />
                          </span>
                          <span>
                            <span className="trainer-hero-person-name">John Trainer</span>
                            <span className="trainer-hero-person-role">Strength Coach</span>
                          </span>
                          <span className="trainer-stars" aria-label="5.0 rating">
                            <HeroStarIcon />
                            <span>5.0</span>
                          </span>
                        </div>
                      </div>
                    </article>

                    <article className="trainer-hero-animated-card trainer-clients-card">
                      <div className="trainer-hero-card-float">
                        <div className="trainer-clients-heading">
                          <span className="trainer-clients-icon">
                            <HeroGroupIcon />
                          </span>
                          <span>
                            <span>Active remote clients</span>
                            <strong>
                              <AnimatedNumber target={18} delay={1750} duration={1100} />
                            </strong>
                          </span>
                        </div>
                        <div className="trainer-card-delta trainer-clients-delta">
                          ↑ 3 this month
                        </div>
                        <div className="trainer-avatar-stack">
                          {[heroPerson2, heroPerson3, heroPerson4, heroPerson5, heroPerson6].map(
                            (avatar) => (
                              <span className="trainer-hero-avatar" key={avatar}>
                                <img src={avatar} alt="" />
                              </span>
                            ),
                          )}
                          <span className="trainer-avatar-more">+8</span>
                        </div>
                      </div>
                    </article>

                    <article className="trainer-hero-animated-card trainer-sarah-card">
                      <div className="trainer-hero-card-float">
                        <div className="trainer-hero-person">
                          <span className="trainer-hero-avatar">
                            <img src={heroPerson2} alt="" />
                          </span>
                          <span>
                            <span className="trainer-hero-person-name">Sarah Mitchell</span>
                            <span className="trainer-hero-person-role">
                              <span className="trainer-online-dot" /> Online
                            </span>
                          </span>
                        </div>
                        <div className="trainer-mini-pills">
                          <span>
                            <span className="trainer-mini-icon">
                              <HeroLaptopIcon />
                            </span>
                            <AnimatedNumber target={12} delay={1900} duration={900} /> online
                            sessions
                          </span>
                          <span>
                            <span className="trainer-mini-icon">
                              <HeroPoundIcon />
                            </span>
                            <AnimatedNumber target={240} prefix="£" delay={2000} duration={1000} />
                            /month
                          </span>
                        </div>
                      </div>
                    </article>

                    <article className="trainer-hero-animated-card trainer-impact-card">
                      <div className="trainer-hero-card-float">
                        <span className="trainer-impact-icon">
                          <HeroPoundIcon />
                        </span>
                        <div>
                          <strong>More clients. More impact</strong>
                          <span>Build recurring income on your terms.</span>
                        </div>
                        <HeroImpactArrow className="trainer-impact-arrow" />
                      </div>
                    </article>

                    <article className="trainer-hero-animated-card trainer-tom-card">
                      <div className="trainer-hero-card-float">
                        <div className="trainer-hero-person">
                          <span className="trainer-hero-avatar">
                            <img src={heroPerson3} alt="" />
                          </span>
                          <span>
                            <span className="trainer-hero-person-name">Tom Edwards</span>
                            <span className="trainer-hero-person-role">
                              <span className="trainer-hybrid-dot" /> Hybrid
                            </span>
                          </span>
                        </div>
                        <div className="trainer-mini-pills">
                          <span>
                            <span className="trainer-mini-icon">
                              <HeroLaptopIcon />
                            </span>
                            <AnimatedNumber target={4} delay={2200} duration={700} /> of 5 sessions
                            done
                          </span>
                          <span>
                            <span className="trainer-mini-icon">
                              <HeroPoundIcon />
                            </span>
                            <AnimatedNumber target={180} prefix="£" delay={2300} duration={900} />
                            package
                          </span>
                        </div>
                      </div>
                    </article>
                  </div>
                </div>
              </div>
            </section>

            <section
              className={`trainer-section trainer-demo-section ${
                isDemoInView ? 'trainer-demo-section-active' : ''
              }`}
              id="trainer-demo"
              ref={demoSectionRef}
            >
              <div className="trainer-landing-container">
                <h2>Try it for yourself</h2>
                <TrainerPlatformDemo isActive={isDemoInView} />
                <div className="trainer-section-actions">
                  <Button
                    variant="primary"
                    onClick={handleGetStarted}
                    svg={<RightArrowIcon />}
                    className="trainer-button trainer-button-primary right-arrow"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </section>

            <section className="trainer-section trainer-steps">
              <div className="trainer-landing-container">
                <h2>Create once. Coach anywhere and Earn more.</h2>
                <p className="trainer-section-copy">
                  Scale your personal training business with AI program creation, remote client
                  tracking and automated insights.
                </p>

                {features.map((feature) => (
                  <div
                    className="trainer-feature-row"
                    data-feature-animation={feature.animation}
                    key={feature.number}
                  >
                    <div className="trainer-feature-copy">
                      <div className="trainer-step-no">{feature.number}</div>
                      <div className="trainer-feature-title">
                        <span className="trainer-feature-icon" aria-hidden="true">
                          <feature.Icon />
                        </span>
                        <h3>{feature.title}</h3>
                      </div>
                      <p>{feature.copy}</p>
                    </div>
                    <div className="trainer-feature-image">
                      <div className={`trainer-feature-art trainer-${feature.animation}-art`}>
                        <img src={feature.image} alt={feature.alt} />
                        <FeatureScreenAnimation type={feature.animation} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="trainer-section trainer-soft-media-section">
              <div className="trainer-landing-container">
                <img
                  className="trainer-wide-art"
                  src={featureGrowEarn}
                  alt="Trainer using miniPT to grow and earn more"
                />
              </div>
            </section>

            <section className="trainer-section">
              <div className="trainer-landing-container">
                <div
                  className="trainer-growth-card"
                  style={{ backgroundImage: `url(${ctaPrograms})` }}
                >
                  <div>
                    <h2>Spend less time building plans. More time being their coach.</h2>
                    <p>
                      Create personalised programs, support clients between sessions and grow your
                      coaching without losing the personal connection.
                    </p>
                    <div className="trainer-actions">
                      <Button
                        variant="secondary"
                        onClick={handleGetStarted}
                        className="trainer-button trainer-button-dark"
                      >
                        Get Started
                      </Button>
                      <Button
                        variant="light"
                        onClick={scrollToDemo}
                        className="trainer-button trainer-button-light"
                      >
                        Try Demo
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="trainer-section trainer-testimonials-section">
              <div className="trainer-landing-container">
                <h2>An “INDISPENSABLE” tool</h2>
                <div className="trainer-testimonials">
                  {testimonials.map((testimonial) => (
                    <article className="trainer-testimonial" key={testimonial.name}>
                      <div className="trainer-icon-box">
                        <QuoteIcon />
                      </div>
                      <p>{testimonial.quote}</p>
                      <div className="trainer-person-row">
                        <span className="trainer-avatar">
                          <img src={testimonial.avatar} alt="" />
                        </span>
                        <span>
                          <span className="trainer-person-name">{testimonial.name}</span>
                          <br />
                          <span className="trainer-person-role">{testimonial.role}</span>
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            <section className="trainer-section trainer-pricing-section" id="trainer-pricing">
              <div className="trainer-landing-container">
                <h2>Pricing</h2>
                <p className="trainer-section-copy">
                  Choose the plan that fits your fitness journey.
                </p>
                <div
                  className="trainer-pricing-grid"
                  onMouseLeave={() => setActivePricingIndex(null)}
                >
                  {pricingPlans.map((plan, index) => {
                    const isActivePricingCard = activePricingIndex === index;

                    return (
                      <article
                        className={`trainer-price-card ${isActivePricingCard ? 'featured' : ''}`}
                        key={plan.title}
                        onMouseEnter={() => setActivePricingIndex(index)}
                        onTouchStart={() => setActivePricingIndex(index)}
                      >
                        <h3>{plan.title}</h3>
                        <div className={`trainer-price ${plan.suffix ? '' : 'trainer-price-pill'}`}>
                          <span>{plan.price}</span>
                          {plan.suffix && <small>{plan.suffix}</small>}
                        </div>
                        <p>{plan.copy}</p>
                        <ul className="trainer-check-list">
                          {plan.features.map((feature) => (
                            <li key={feature}>{feature}</li>
                          ))}
                        </ul>
                      </article>
                    );
                  })}
                </div>
                <div className="trainer-price-action">
                  <Button
                    variant="primary"
                    onClick={handleGetStarted}
                    svg={<RightArrowIcon />}
                    className="trainer-button trainer-button-primary right-arrow w-100"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </section>

            <section className="trainer-section trainer-faq-section" id="trainer-faq">
              <div className="trainer-landing-container trainer-faq-grid">
                <div>
                  <h2>Get all your questions answered here</h2>
                  <p>
                    We tried to answer all frequent questions. Still confused? Feel free to contact
                    us.
                  </p>
                </div>
                <div className="trainer-faq-list">
                  {faqs.map((faq, index) => (
                    <article
                      className={`trainer-faq-item ${openFaqIndex === index ? 'open' : ''}`}
                      key={faq.question}
                    >
                      <button
                        className="trainer-faq-question"
                        type="button"
                        onClick={() => setOpenFaqIndex(openFaqIndex === index ? -1 : index)}
                      >
                        <span>{faq.question}</span>
                        <img
                          className="trainer-faq-icon"
                          src={openFaqIndex === index ? faqCollapseIcon : faqExpandIcon}
                          alt=""
                          aria-hidden="true"
                        />
                      </button>
                      <div className="trainer-faq-answer">{faq.answer}</div>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}

        {step === 2 && (
          <div className="animation-content max-width-768 animation-text-position">
            <TextAnimate
              animation="slideUp"
              by="word"
              as="h2"
              center={true}
              className="page-intro-title mt-0"
              duration={1}
              delay={0.05}
              onComplete={() => {
                setTimeout(() => {
                  gsap.to('.animation-content', { opacity: 0, duration: 0.5 });

                  const isMobile = window.innerWidth < 768;
                  gsap.to(videoContainerRef.current, {
                    top: isMobile ? '16px' : '40px',
                    left: '50%',
                    y: 0,
                    yPercent: 0,
                    xPercent: -50,
                    width: isMobile ? 130 : 200,
                    height: isMobile ? 130 : 200,
                    duration: 1.2,
                    ease: 'power3.inOut',
                    onComplete: () => {
                      setStep(3);
                    },
                  });
                }, 1500);
              }}
            >
              {`Hi ${firstName}! Let's personalize\nyour experience`}
            </TextAnimate>
          </div>
        )}

        <div className="hero-glow-video-container flex-center-column" ref={videoContainerRef}>
          <video
            ref={videoRef}
            src={MiniPTGIF}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="hero-glow-video"
            onLoadedData={(e) => {
              e.currentTarget.muted = true;
              e.currentTarget.play().catch(() => {});
            }}
          />
          <span
            ref={videoTextRef}
            className="mini-pt-text"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            MiniPT Core
          </span>
        </div>

        {step === 3 && (
          <div style={{ width: '100%', zIndex: 20 }}>
            <Experience onNext={() => setStep(4)} />
          </div>
        )}

        {step === 4 && (
          <div style={{ width: '100%', zIndex: 20 }}>
            <ClientType onBack={() => setStep(3)} onNext={(_ids) => setStep(5)} />
          </div>
        )}

        {step === 5 && (
          <div style={{ width: '100%', zIndex: 20 }}>
            <Offer onBack={() => setStep(4)} onNext={(_offers) => setStep(6)} />
          </div>
        )}

        {step === 6 && (
          <div style={{ width: '100%', zIndex: 20 }}>
            <Schedule mode="onboarding" onBack={() => setStep(5)} onNext={() => setStep(7)} />
          </div>
        )}

        {step === 7 && (
          <div style={{ width: '100%', zIndex: 20 }}>
            <Step6AIHelp onComplete={() => navigate('/onboarding-trainer/dashboard')} />
          </div>
        )}
      </div>
    </>
  );
};
